import { useState } from 'react';
import Menu from '@mui/material/Menu';
import { useAppState } from '../../app/state';
import type { TopbarControl } from './topbarControls';

/**
 * One "…" overflow menu — the top bar grows one per side when its controls no longer fit
 * (plan 1.3), and these are the first MUI components the app renders (Menu/MenuItem, themed
 * in `theme/theme.ts` to be indistinguishable from `.filterpanel`).
 *
 * The component renders NOTHING when it is handed no controls, which is what makes the two
 * placement rules fall out of the data: the left "…" exists only in edit mode because the
 * left's overflowable controls are only visible in edit mode, and neither "…" exists at all
 * until the fit controller has actually pulled something.
 *
 * The MENU renders from the same registry definitions the inline buttons render from — one
 * definition per control, living in exactly one of the two places at a time. MUI's Menu
 * handles Escape, outside-click, focus return and keyboard navigation; it portals to
 * `document.body`, which is why the theme bridge (not this file) carries its styling — an
 * open menu restyles when the theme flips because the ThemeProvider re-renders the portal's
 * contents in place.
 */
export function TopbarOverflowMenu({
  side,
  controls,
}: {
  side: 'left' | 'right';
  controls: readonly TopbarControl[];
}) {
  const s = useAppState();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (controls.length === 0) return null;
  const close = () => setAnchorEl(null);

  return (
    <>
      <button
        className="btn"
        id={`tb-ovf-${side}`}
        title="More actions"
        aria-haspopup="menu"
        aria-expanded={!!anchorEl}
        onClick={(e) => {
          const btn = e.currentTarget;
          setAnchorEl((open) => (open ? null : btn));
        }}
      >
        <svg className="ico">
          <use href="#i-more" />
        </svg>
      </button>
      <Menu
        id={`tb-ovf-${side}-menu`}
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        marginThreshold={8}
        // The page never scrolls (body is overflow:hidden), so the lock would only add a
        // phantom scrollbar-width padding jolt to the fixed top bar.
        disableScrollLock
      >
        {controls.flatMap((c) => c.menuItems(s, close))}
      </Menu>
    </>
  );
}
